import { ApplicationContext, CompanyUser } from "../models/application-context.model";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

export type CompanyUserEmailLookup = ReadonlyMap<string, string>;

export interface CompanyUserAuditLookups {
  emailLookup: CompanyUserEmailLookup;
  idLookup: ReadonlyMap<string, string>;
}

function companyUserDisplayName(
  user: { firstName?: string; lastName?: string } | null | undefined
): string {
  return [user?.firstName, user?.lastName]
    .filter((part) => String(part ?? "").trim())
    .join(" ")
    .trim();
}

function registerCompanyUser(
  user:
    | (CompanyUser & { email?: string })
    | { id?: string; firstName?: string; lastName?: string; email?: string }
    | null
    | undefined,
  emailLookup: Map<string, string>,
  idLookup: Map<string, string>
): void {
  if (!user) {
    return;
  }
  const name = companyUserDisplayName(user);
  if (!name) {
    return;
  }
  const email = user.email?.trim().toLowerCase();
  if (email) {
    emailLookup.set(email, name);
  }
  const id = String(user.id ?? "").trim();
  if (id) {
    idLookup.set(id, name);
    idLookup.set(id.toLowerCase(), name);
  }
}

export function buildCompanyUserEmailDisplayLookup(
  users: ReadonlyArray<{
    firstName?: string;
    lastName?: string;
    email?: string;
  }>
): CompanyUserEmailLookup {
  const map = new Map<string, string>();
  for (const u of users) {
    registerCompanyUser(u, map, new Map());
  }
  return map;
}

export function buildCompanyUserAuditLookups(
  ctx: ApplicationContext | null | undefined
): CompanyUserAuditLookups {
  const emailLookup = new Map<string, string>();
  const idLookup = new Map<string, string>();
  for (const user of ctx?.allAvailableUsers ?? []) {
    registerCompanyUser(user, emailLookup, idLookup);
  }
  for (const user of Object.values(ctx?.companyUsersMap ?? {})) {
    registerCompanyUser(user, emailLookup, idLookup);
  }
  registerCompanyUser(ctx?.currentlyLoggedInUser, emailLookup, idLookup);
  return { emailLookup, idLookup };
}

/** Maps audit tokens (email or user id from PHI) to a display name; never returns an email. */
export function resolveAuditDisplayName(
  token: string | undefined | null,
  lookups?: CompanyUserAuditLookups,
  fallback = "—"
): string {
  const raw = String(token ?? "").trim();
  if (!raw) {
    return fallback;
  }

  const emailLookup = lookups?.emailLookup;
  const idLookup = lookups?.idLookup;

  if (EMAIL_RE.test(raw)) {
    const name = emailLookup?.get(raw.toLowerCase())?.trim();
    return name || fallback;
  }

  const idName =
    idLookup?.get(raw)?.trim() || idLookup?.get(raw.toLowerCase())?.trim();
  if (idName) {
    return idName;
  }

  if (raw.includes("@")) {
    return fallback;
  }

  return raw;
}
